import DOMPurify from "dompurify";

/** True when a stored description contains HTML from the rich text editor. */
export function isRichHtml(value: string | null | undefined): boolean {
  return !!value && /<(p|ul|ol|li|strong|em|u|b|i|br|h[1-6])[\s>/]/i.test(value);
}

export function sanitizeRichHtml(value: string): string {
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "ul", "ol", "li", "h1", "h2", "h3"],
    ALLOWED_ATTR: [],
  });
}

/** Convert legacy plain text into editor HTML (keeps line breaks). */
export function plainToHtml(value: string | null | undefined): string {
  if (!value) return "";
  if (isRichHtml(value)) return value;
  const esc = value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc.split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("");
}

/** Visible text length (for character limits). */
export function richTextLength(value: string | null | undefined): number {
  if (!value) return 0;
  if (!isRichHtml(value)) return value.length;
  return value.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").length;
}

export interface RichRun { text: string; bold?: boolean; italics?: boolean; underline?: boolean }
export interface RichBlock { runs: RichRun[]; bullet?: string; heading?: boolean }

/** Break rich HTML (or plain text) into blocks of styled runs, for Word export. */
export function richTextToBlocks(value: string | null | undefined): RichBlock[] {
  if (!value) return [];
  if (!isRichHtml(value) || typeof DOMParser === "undefined") {
    return value.split(/\n/).map((line) => ({ runs: [{ text: line }] }));
  }
  const doc = new DOMParser().parseFromString(sanitizeRichHtml(value), "text/html");
  const blocks: RichBlock[] = [];
  const collect = (node: Node, style: Omit<RichRun, "text">, runs: RichRun[]) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        const t = child.textContent || "";
        if (t) runs.push({ text: t, ...style });
      } else if (child instanceof HTMLElement) {
        const tag = child.tagName.toLowerCase();
        if (tag === "br") { runs.push({ text: "\n", ...style }); return; }
        if (tag === "ul" || tag === "ol") return; // nested lists handled by walker
        collect(child, {
          bold: style.bold || tag === "strong" || tag === "b",
          italics: style.italics || tag === "em" || tag === "i",
          underline: style.underline || tag === "u",
        }, runs);
      }
    });
  };
  const walk = (parent: Element) => {
    Array.from(parent.children).forEach((el) => {
      const tag = el.tagName.toLowerCase();
      if (tag === "ul" || tag === "ol") {
        Array.from(el.children).forEach((li, i) => {
          const runs: RichRun[] = [];
          collect(li, {}, runs);
          blocks.push({ runs, bullet: tag === "ol" ? `${i + 1}. ` : "• " });
          li.querySelectorAll(":scope > ul, :scope > ol").forEach((n) => walk({ children: [n] } as any));
        });
      } else {
        const runs: RichRun[] = [];
        collect(el, {}, runs);
        blocks.push({ runs, heading: /^h[1-3]$/.test(tag) });
      }
    });
  };
  walk(doc.body);
  return blocks.filter((b) => b.runs.some((r) => r.text.trim()));
}

/** Rich HTML (or plain text) as one line of readable text, for short previews. */
export function richTextToPlain(value: string | null | undefined): string {
  if (!value) return "";
  if (!/<[a-z][\s\S]*>|&[a-z#0-9]+;/i.test(value)) return value;
  const hasDom = typeof DOMParser !== "undefined";
  const spaced = (hasDom ? sanitizeRichHtml(value) : value).replace(/<br\s*\/?>|<\/(p|li|h[1-6])>/gi, " ");
  const text = hasDom
    ? new DOMParser().parseFromString(spaced, "text/html").body.textContent || ""
    : spaced.replace(/<[^>]+>/g, "").replace(/&amp;/g, "&").replace(/&nbsp;/g, " ");
  return text.replace(/\s+/g, " ").trim();
}
